'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  requestPasswordResetSchema,
  type RequestPasswordResetInput,
} from '@/lib/validators/auth.schema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

export function ForgotPasswordForm() {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestPasswordResetInput>({
    resolver: zodResolver(requestPasswordResetSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: RequestPasswordResetInput) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      });
      if (res.status === 429) {
        toast.error(t('forgot.tooManyRequests'));
        return;
      }
      // The endpoint is enumeration-safe and always returns a generic success.
      setSubmitted(true);
    } catch {
      toast.error(t('somethingWrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-md border border-[#22c55e]/30 bg-[#22c55e]/10 p-4 text-sm text-[#166534]">
        <p className="font-medium">{t('forgot.checkInboxTitle')}</p>
        <p className="mt-1">
          {t('forgot.checkInboxBody')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm text-[#6b7280]">
          {tc('email')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder={t('fields.emailPlaceholder')}
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.email}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" className="text-sm text-[#ef4444]">
            {errors.email.message}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="h-[44px] w-full bg-[#fc800a] text-white font-bold hover:bg-[#e07209] disabled:opacity-40 disabled:bg-[#fc800a]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">{t('sending')}</span>
            {t('sending')}
          </>
        ) : (
          t('forgot.submit')
        )}
      </Button>
    </form>
  );
}
